import React from "react";
import { Print as PrintIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import ThermalPrinterTest, { PrintData } from "src/print-configuration/PrintConfigration";
import { Order } from "src/shared/services/interfacev2";
import CommonButtons from "src/shared/components/CommonButtons";

interface OrderHeaderProps {
  order: Order;
  outstandingAmount: number;
  onPrint: () => void;
  onGoBack: () => void;
  printData: PrintData;
}

const OutstandingBalanceCard: React.FC<{ outstandingAmount: number }> = ({ outstandingAmount }) => {
  const hasOutstanding = outstandingAmount > 0;

  return (
    <div
      className={`px-3 py-1.5 flex items-center gap-2 rounded-lg transition-all duration-200 whitespace-nowrap
      ${hasOutstanding ? "bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500" : "bg-gradient-to-r from-green-50 to-green-100 border-l-4 border-green-500"}`}
    >
      <span className="font-semibold text-gray-700 text-xs sm:text-sm">Outstanding:</span>
      <span className={`font-bold text-sm sm:text-base ${hasOutstanding ? "text-red-600" : "text-green-600"}`}>{outstandingAmount?.toFixed(2) || "0.00"} QAR</span>
    </div>
  );
};

const OrderHeader: React.FC<OrderHeaderProps> = ({ order, outstandingAmount, onGoBack, onPrint, printData }) => {
  const getDisplayStatus = () => {
    const status = order?.orders?.[0]?.status || "Pending";
    // Display "Completed" instead of backend "Delivered"
    return status === "Delivered" ? "Completed" : status;
  };

  const displayStatus = getDisplayStatus();

  const getStatusConfig = () => {
    switch (displayStatus) {
      case "Completed":
        return {
          bg: "bg-green-100",
          text: "text-green-800",
          icon: "✓",
        };
      case "Cancelled":
        return {
          bg: "bg-red-100",
          text: "text-red-800",
          icon: "✗",
        };
      default:
        return {
          bg: "bg-gray-100",
          text: "text-gray-800",
          icon: "•",
        };
    }
  };

  const statusConfig = getStatusConfig();

  // ✅ Use outstandingAmount from prop instead of order.totalOutstandingBalance
  const displayOutstanding = outstandingAmount > 0 ? outstandingAmount : order?.totalOutstandingBalance || 0;

  return (
    <div className="w-full bg-white/80 backdrop-blur-md rounded-lg border border-gray-200 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 px-2 py-2">
        {/* Left Section - Back button, Order Info & Status */}
        <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
          <CommonButtons label="Back" onClick={onGoBack} icon={ArrowBackIcon} variant="secondary" />
        </div>

        {/* Center/Middle Section - Outstanding Balance */}
        <div className="flex-shrink-0">
          <OutstandingBalanceCard outstandingAmount={displayOutstanding} />
        </div>

        {/* Right Section - Print Button */}
        <div className="flex-shrink-0">
          <ThermalPrinterTest PrintData={printData} BtnName="PRINT" icon={PrintIcon} />
        </div>
      </div>
    </div>
  );
};

export default OrderHeader;
